import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AuthLayouts from "layouts/AuthLayouts";
import NavbarAuth from "components/organism/Navbar/NavbarAuth";
import { toast } from "react-hot-toast";
import { signInWithEmailAndPassword } from "firebase/auth";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useNavigate } from "react-router-dom";
import { useGoogleOAuth } from "hooks/useLogin";
import { auth } from "../../firebase/firebaseConfig";
import { ErrorText } from "components/atoms/ErrorText";

const SignIn = () => {
    const navigate = useNavigate();
    const token = window.localStorage.getItem("token");

    useEffect(() => {
        if (token) return navigate("/dashboard", { replace: true });
    }, [token]);

    const { SignInWithGoogle, isGoogleLoading } = useGoogleOAuth();
    const [isLoginLoading, setIsLoading] = useState(false);
    const initialValues = { email: "", password: "" };

    const disabledFn = () => {
        if (email && password) return false;
        return true;
    };

    const schemaValidations = Yup.object({
        email: Yup.string()
            .email("Invalid Email Address")
            .required("Field Email Required"),
        password: Yup.string()
            .min(8, "Password must be more than 8 character")
            .required("Field Password Required")
    });

    const SignInHandler = async (values) => {
        setIsLoading(true);

        try {
            const userCredentials = await signInWithEmailAndPassword(auth, values.email, values.password);
            const user = userCredentials.user;

            console.log(user);
            window.localStorage.setItem("token", user.accessToken);

            setIsLoading(false);

            toast.success("Sign In Successfully");
            setTimeout(() => {
                navigate("/dashboard", { replace: true });
            }, 2000);
        } catch (error) {
            console.log(error);
            setIsLoading(false);

            toast.error("Sign In Failed");
        }
    };

    const [showPassword, setShowPassword] = useState(false);

    const ShowPassword = () => setShowPassword(true);
    const HidePassword = () => setShowPassword(false);

    return (
        <Fragment>
            <AuthLayouts title="Sign In">
                <div className="w-full max-w-[1440px] mx-auto flex flex-col md:flex-row bg-neutral-20 items-start justify-between">
                    <div className="w-full bg-neutral-20">
                        <NavbarAuth />
                        <div className="phone:px-6 phone:pt-8 tablet:pt-[92px] pt-[72px] bg-neutral-10 pb-[136px] phone:pb-[73px] tablet:pl-8 tablet:pr-8 pl-[120px] pr-[108px]">
                            <h3 className="font-semibold text-neutral-100 text-heading-4 tracking-text leading-[42px]">Sign In</h3>
                            <p className="text-neutral-50 font-normal text-md phone:text-sm mt-2">Welcome back! Please enter your details</p>
                            <Formik initialValues={initialValues} validationSchema={schemaValidations} onSubmit={SignInHandler}>
                                {({ errors, touched }) => (
                                    <Form>
                                        <div className="w-full mt-8 phone:mt-6">
                                            <h3 className={clsx(
                                                "font-normal phone:tracking-[0px] phone:text-sm text-md text-neutral-50 tracking-text pb-4 tablet:pb-3 phone:pb-3 leading-[16px]"
                                            )}>Email <span className="text-semantic-danger-main">*</span></h3>
                                            <Field type="email" name="email" className={clsx(
                                                "w-full placeholder:antialiased h-[56px] px-4 bg-neutral-20 rounded-lg ",
                                                errors.email && touched.email ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                            )} />
                                            <ErrorMessage name="email" component={ErrorText} />
                                        </div>
                                        <div className="relative w-full mt-5 phone:mt-4">
                                            <h3 className={clsx(
                                                "font-normal phone:tracking-[0px] phone:text-sm text-md text-neutral-50 tracking-text pb-4 tablet:pb-3 phone:pb-3 leading-[16px]"
                                            )}>Password <span className="text-semantic-danger-main">*</span></h3>
                                            <Field type={showPassword ? "text" : "password"} name="password" className={clsx(
                                                "w-full antialiased placeholder:antialiased h-[56px] px-4 bg-neutral-20 rounded-lg",
                                                errors.password && touched.password ? "border border-red-600 !bg-neutral-10" : "border-neutral-20"
                                            )} />
                                            {showPassword ?
                                                <img onClick={HidePassword} src="/icons/universal/eye.svg" className="hover:cursor-pointer absolute top-[49px] right-4 w-6 h-6" /> :
                                                <img onClick={ShowPassword} src="/icons/universal/eye-slash.svg" className="hover:cursor-pointer absolute top-[49px] right-4 w-6 h-6" />
                                            }
                                            <ErrorMessage name="password" component={ErrorText} />
                                        </div>
                                        <div className="w-full phone:mt-4 mt-5 flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <input type="checkbox" className="w-5 h-5 rounded-[4px] checked:bg-primary-main focus:outline-none border border-neutral-30 checked:outline-none checked:border-none checked:ring-transparent hover:cursor-pointer focus:ring-transparent" />
                                                <h3 className="font-medium phone:tracking-[0px] phone:text-sm text-md text-neutral-100 tracking-text leading-[16px]">Remember me</h3>
                                            </div>
                                            <Link to="/auth/forgot-password">
                                                <h3 className="font-medium phone:tracking-[0px] phone:text-sm text-md text-primary-main tracking-text leading-[16px] hover:cursor-pointer">Forgot Password?</h3>
                                            </Link>
                                        </div>
                                        <button disabled={disabledFn} className="w-full h-[56px] flex items-center justify-center bg-primary-main rounded-lg text-neutral-10 font-semibold text-md tracking-text-sm phone:mt-6 mt-8 hover:bg-primary-hover">{isLoginLoading ? "Loading..." : "Sign In"}</button>
                                    </Form>
                                )}
                            </Formik>
                            <div className="relative flex py-5 items-center">
                                <div className="flex-grow border-t border-neutral-30"></div>
                                <h3 className="font-normal phone:tracking-[0px] phone:text-sm text-sm text-neutral-50 tracking-text leading-[16px] uppercase px-4 tablet:leading-[19.83px] phone:leading-[19.83px]">Or</h3>
                                <div className="flex-grow border-t border-neutral-30"></div>
                            </div>
                            <button onClick={SignInWithGoogle} className="w-full h-[56px] flex items-center justify-center hover:bg-neutral-20 hover:border-neutral-20 border border-neutral-30 bg-neutral-10 rounded-lg text-neutral-100 font-semibold text-md tracking-text-sm tablet:leading-[19.83px] phone:leading-[19.83px] leading-[19.83px]">
                                <img src="/images/auth/google.svg" className="pr-[14px]" />
                                {isGoogleLoading ? "Authenticating..." : "Sign in with Google"}
                            </button>
                            <h3 className="font-normal phone:tracking-[0px] phone:text-md text-md text-neutral-50 tracking-text phone:mt-8 tablet:mt-8 mt-[26px] leading-[16px]">Don’t have an account?
                                <Link to="/auth/signup">
                                    <span className="font-semibold text-primary-main leading-[19.83px] hover:cursor-pointer"> Sign Up</span>
                                </Link>
                            </h3>
                        </div>
                    </div>
                    <div className="hidden md:block w-full bg-neutral-20 phone:py-0 phone:bg-neutral-10 mt-[-30px] phone:px-6 tablet:pl-6 tablet:pr-8 pl-[78px] pr-[83px] pb-[159px] pt-[196px]">
                        <img src="/images/auth/signin-hero.png" className="hero-login w-[614px] h-[545px] tablet:w-[466px] tablet:h-[414px]" />
                    </div>
                </div>
            </AuthLayouts>
        </Fragment>
    );
};

export default SignIn;